import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormWrapper, FormRow, Label, InputField, SubmitButton, PageContainer, ContentContainer, CancelButton, DateInputField } from "../styles/ComponentStyles";
import FlashMessage from "../components/FlashMessage";
import PaymentAttachments from "../components/PaymentAttachments";
import usePayment from "../hooks/usePayment";
import apiService from "../services/apiService";
import { readPaymentAttachments } from "../utils/paymentAttachments";

const EditPaymentPage = () => {
  const [flashMessage, setFlashMessage] = useState('');
  const { paymentId } = useParams();
  // usePayment walks the payments list until it finds this id. The old call was
  // usePayments(1) — page 1 at a limit of 100 — while the list paginates 10 to a
  // page, so anything past row 100 opened a blank form and said nothing.
  const { payment, loading, error, notFound } = usePayment(paymentId);
  const navigate = useNavigate();

  // Attachments are their own thing: they are saved the moment they are uploaded,
  // not on "Save Changes", and they are deliberately kept out of formData so a
  // failed upload cannot disturb what the user has typed into the form.
  const [attachments, setAttachments] = useState([]);

  const [formData, setFormData] = useState({
    biller: '',
    payment_date: '',
    payment_amount: '',
    payment_charge: '',
    payment_method: '',
    remarks: '',
    source: '',
  });

  const handleFlashMessage = (message) => {
    setFlashMessage(message);
  };

  // Set formData once the payment has actually been found.
  useEffect(() => {
    if (payment) {
      setFormData({
        biller: payment.biller,
        payment_date: payment.payment_date,
        payment_amount: payment.payment_amount,
        payment_charge: payment.payment_charge,
        payment_method: payment.payment_method,
        remarks: payment.remarks,
        source: payment.source,
      });
      // The payments list Title-Cases its keys, so this may arrive as
      // `Attachments`; readPaymentAttachments knows both spellings.
      setAttachments(readPaymentAttachments(payment));
    }
  }, [payment]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleCancel = () => {
    navigate('/payments');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFlashMessage('');
    try {
      let payload = { ...formData };

      // Remove usual_due_date_day from payload if it's not provided
      // if (!payload.usual_due_date_day) {
      //   delete payload.usual_due_date_day;
      // }

      const result = await apiService.putPayment(paymentId, payload);
      handleFlashMessage(result.message + " Redirecting. . .");
      setTimeout(() => {
        navigate("/payments");
      }, 2000);
    } catch (error) {
      // Log the error message for debugging
      if (error.response) {
        console.error("Error response:", error.response.data);
        handleFlashMessage(error.response.data.detail);
      } else {
        console.error("Error:", error.message);
        handleFlashMessage(error.message);
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error loading payments data</div>;

  // "We looked and it isn't there" is an answer, not a failure — and it is the
  // one this page used to give as a form full of empty inputs.
  if (notFound) {
    return (
      <PageContainer>
        <ContentContainer>
          <FormWrapper as="div" className='cash-flows-form-wrapper'>
            <h3>Payment not found</h3>
            <p>
              This payment is not in your payments list any more — it may have been
              deleted, or it belongs to a record you no longer have.
            </p>
            <CancelButton type="button" onClick={handleCancel}>
              Back to Payments
            </CancelButton>
          </FormWrapper>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        {flashMessage && <FlashMessage message={flashMessage} />}
        <FormWrapper onSubmit={handleSubmit} className='cash-flows-form-wrapper'>
          <h3>Edit Payment*</h3>
          <FormRow>
            <Label htmlFor="biller">Biller (Edit this in Bills Tab)</Label>
            <InputField
              name='biller'
              value={formData.biller}
              onChange={handleChange}
              readOnly={true}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payment_date">Payment Date</Label>
            <DateInputField
              name='payment_date'
              value={formData.payment_date}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payment_amount">Payment Amount</Label>
            <InputField
              name='payment_amount'
              value={formData.payment_amount}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payment_charge">Payment Charge</Label>
            <InputField
              name='payment_charge'
              type='number'
              step='0.01'
              value={formData.payment_charge}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="payment_method">Payment Method</Label>
            <InputField
              name='payment_method'
              value={formData.payment_method}
              onChange={handleChange}
            />
          </FormRow>
          <FormRow>
            <Label htmlFor="remarks">Remarks</Label>
            <InputField
              name='remarks'
              value={formData.remarks}
              onChange={handleChange}
            />
          </FormRow>
          {/* Provenance, not something the user types. It used to render the
              literal 'Frontend' for every payment while submitting whatever was
              actually stored, so the field and the payload disagreed on screen. */}
          <FormRow>
            <Label htmlFor="source">Source (where this payment was recorded)</Label>
            <InputField
              name='source'
              value={formData.source || ''}
              onChange={handleChange}
              readOnly={true}
            />
          </FormRow>
          <SubmitButton type="submit" onClick={handleSubmit}>Save Changes</SubmitButton>
          <CancelButton type="button" onClick={handleCancel}>Cancel</CancelButton>
        </FormWrapper>
        {/* Outside the form on purpose — a file input and its buttons inside it
            would sit one stray Enter key away from submitting the payment. */}
        <PaymentAttachments
          paymentId={paymentId}
          attachments={attachments}
          onChange={setAttachments}
          onFlashMessage={handleFlashMessage}
        />
      </ContentContainer>
    </PageContainer>
  );
};

export default EditPaymentPage;
